<template>
	<div class="patient-management">
		<template v-if="loadingPage">
			<v-row no-gutters justify="center" align="center" style="height: 600px;">
				<v-progress-circular
					:size="50"
					color="primary"
					indeterminate
				></v-progress-circular>
			</v-row>
		</template>
		<v-container fluid v-else>
			<v-row no-gutters class="px-3">
				<v-col cols="12">
					<v-row no-gutters align="center" class="mt-3 mb-3">
						<span class="text-subtitle-1 font-weight-regular primary--text" v-translate key="patient-info">Data Consent</span>
					</v-row>
					<v-divider class="mb-0"></v-divider>
				</v-col>
				<v-col cols="12" v-if="!existDataConsent">
					<v-row no-gutters>
						<v-col cols="12">
							<v-btn small fab class="mt-3" color="primary" @click="newDataConsent = !newDataConsent; consentType = 'data'; newTerapeuticConsent = false">
								<v-icon>mdi-plus</v-icon>
							</v-btn>
						</v-col>
						<v-col v-if="newDataConsent" cols="12">
							<v-btn key="upload-consent" color="primary" class="mt-4 ml-2" @click="clickUpload('data')">
								<v-icon left>mdi-upload</v-icon>
								<translate>Upload</translate>
							</v-btn>
							<v-btn key="show-consent" color="primary" class="mt-4 ml-2" @click="clickConsent('data')">
								<v-icon left>mdi-file-document-edit-outline</v-icon>
								<translate>Consent</translate>
							</v-btn>
							<v-row v-if="showDataUpload" class="mt-3" no-gutters>
								<v-col cols="6">
									<VueFileAgent
										ref="vueFileAgent"
										:theme="'list'"
										:multiple="false"
										:deletable="true"
										:meta="true"
										:accept="'image/*,.pdf,.doc,.docx'"
										:maxSize="'50MB'"
										:maxFiles="1"
										:helpText="$gettext('Drag or click')"
										:errorText="{
											type: 'Invalid file type. Only images and PDFs are allowed',
											size: 'Files cannot exceed 50MB in size'
										}"
										@select="filesSelected($event)"
										@beforedelete="onBeforeDelete($event)"
										v-model="fileRecords"
									></VueFileAgent>
								</v-col>
							</v-row>
						</v-col>
					</v-row>
				</v-col>
				<v-col cols="12" v-else>
					<v-list flat>
						<ConsentItem
							:consent="patient.data_consent_blob"
							:isData="true"
							@delete="deleteDataConsent"
						/>
					</v-list>
				</v-col>
			</v-row>
			<v-row no-gutters class="px-3">
				<v-col cols="12">
					<v-row no-gutters align="center" class="mt-3 mb-3">
						<span class="text-subtitle-1 font-weight-regular primary--text" v-translate key="patient-info">Terapeutic Consents</span>
					</v-row>
					<v-divider class="mb-0"></v-divider>
				</v-col>
				<v-col cols="12">
					<v-row no-gutters>
						<v-col cols="12">
							<v-btn small fab class="mt-3" color="primary" @click="newTerapeuticConsent = !newTerapeuticConsent; consentType = 'terapeutic'; newDataConsent = false">
								<v-icon>mdi-plus</v-icon>
							</v-btn>
						</v-col>
						<v-col v-if="newTerapeuticConsent" cols="12" class="mt-3">
							<v-row no-gutters>
								<v-col cols="4">
									<v-form ref="specForm">
										<v-select
											:items="types"
											:label="$gettext('Type')"
											v-model="terapeuticConsent"
											class="px-2"
											outlined
											required
											dense
											:rules="rules"
											item-text="title"
											return-object
										></v-select>
									</v-form>
								</v-col>
								<v-btn key="upload-consent" color="primary" @click="clickUpload('terapeutic')">
									<v-icon left>mdi-upload</v-icon>
									<translate>Upload</translate>
								</v-btn>
								<v-btn key="show-consent" color="primary" class="ml-2" @click="clickConsent('terapeutic')">
									<v-icon left>mdi-file-document-edit-outline</v-icon>
									<translate>Consent</translate>
								</v-btn>
							</v-row>
							<v-row v-if="showTerapeuticUpload" class="mt-3" no-gutters>
								<v-col cols="6">
									<VueFileAgent
										ref="vueFileAgent"
										:theme="'list'"
										:multiple="false"
										:deletable="true"
										:meta="true"
										:accept="'image/*,.pdf,.doc,.docx'"
										:maxSize="'50MB'"
										:maxFiles="1"
										:helpText="$gettext('Drag or click')"
										:errorText="{
											type: 'Invalid file type. Only images and PDFs are allowed',
											size: 'Files cannot exceed 50MB in size'
										}"
										@select="filesSelected($event)"
										@beforedelete="onBeforeDelete($event)"
										v-model="fileRecords"
									></VueFileAgent>
								</v-col>
							</v-row>
						</v-col>
					</v-row>
				</v-col>
				<v-col cols="12" v-if="existTerapeuticConsent">
					<v-list flat>
						<ConsentItem
							v-for="consent in patient.terapeutic_consents"
							:key="consent.id"
							:consent="consent"
							@delete="deleteTerapeuticConsent"
						/>
					</v-list>
				</v-col>
			</v-row>
			<v-dialog v-model="showConsent" width="70%" scrollable>
				<v-card :loading="loadingConsent">
					<v-card-title class="title primary--text mb-2 secondary">
						<v-icon class="mr-3" color="primary">mdi-file-document-edit</v-icon>
						<template v-if="consentType == 'data'">
							<translate>Data Consent</translate>
						</template>
						<template v-else>
							<span class="mr-1" v-translate>Terapeutic Consent</span>-<span class="ml-1" v-if="terapeuticConsent">{{terapeuticConsent.title}}</span>
						</template>
					</v-card-title>
					<v-card-text v-if="!loadingConsent">
						<v-form ref="form" v-model="valid">
							<v-row no-gutters>
								<v-col cols="12">
									<v-row no-gutters align="center" class="mt-3 mb-3">
											<span class="text-subtitle-1 font-weight-regular primary--text" v-translate key="patient-info">Data of the person who consents</span>
									</v-row>
									<v-divider class="mb-3"></v-divider>
								</v-col>
								<v-col cols="12" sm="6">
									<v-text-field
										v-model="patient.name"
										:rules="nameRules"
										:label="$gettext('Name')"
										class="px-2"
										filled
										placeholder=" "
									/>
								</v-col>
								<v-col cols="12" sm="3">
									<v-text-field v-model="patient.id_number" :label="$gettext('ID Number')" class="pr-2" filled placeholder=" " />
								</v-col>
								<v-col cols="12" sm="3">
									<v-text-field v-model="patient.fiscal" :label="$gettext('VAT Number')" class="pr-2" filled placeholder=" " />
								</v-col>
								<v-col cols="12">
									<v-row no-gutters align="center" class="mt-3 mb-3">
										<v-checkbox
											v-model="legalRepresentative"
											class="mt-0 pt-0"
											color="primary"
											dense
											hide-details
										>
											<template slot="label">
												<span v-translate class="mr-1 primary--text">Legal Representative</span>
											</template>
										</v-checkbox>
									</v-row>
								</v-col>
								<template v-if="legalRepresentative">
									<v-col cols="12">
										<v-divider class="mb-3"></v-divider>
									</v-col>
									<v-col cols="12" sm="6">
										<v-text-field
											v-model="patient.representative_name"
											:rules="nameRules"
											:label="$gettext('Name')"
											class="px-2"
											filled
											placeholder=" "
										/>
									</v-col>
									<v-col cols="12" sm="3">
										<v-text-field v-model="patient.representative_id_number" :label="$gettext('ID Number')" class="pr-2" filled placeholder=" " />
									</v-col>
									<v-col cols="12" sm="3">
										<v-text-field v-model="patient.representative_fiscal" :label="$gettext('VAT Number')" class="pr-2" filled placeholder=" " />
									</v-col>
								</template>
								<v-col cols="12">
									<v-row no-gutters>
										<v-menu
											v-model="dateMenu"
											:close-on-content-click="false"
											:nudge-right="40"
											transition="scale-transition"
											offset-y
											min-width="290px"
										>
											<template v-slot:activator="{ on }">
												<v-text-field
													v-model="date"
													append-icon="event"
													readonly
													v-on="on"
													outlined
													dense
													style="max-width: 150px"
												></v-text-field>
											</template>
											<v-date-picker color="primary" v-model="date" @input="dateMenu = false"></v-date-picker>
										</v-menu>
									</v-row>
								</v-col>
								<v-col cols="12">
									<v-row no-gutters align="center" class="mt-3 mb-3">
											<span class="text-subtitle-1 font-weight-regular primary--text" v-translate key="consent">Consent</span>
									</v-row>
									<v-divider class="mb-3"></v-divider>
								</v-col>
								<v-col cols="12">
									<RichTextEditor ref="editor" :text="body" :consentType="consentType" :consent="true" :isMinor="isMinor" />
								</v-col>
							</v-row>
						</v-form>
						<v-row no-gutters class="mt-2">
							<v-col cols="12">
								<v-row no-gutters align="center" class="mt-3 mb-3">
									<span class="text-subtitle-1 font-weight-regular primary--text" v-translate key="signature">Signature</span>
								</v-row>
								<v-divider class="mb-2"></v-divider>
							</v-col>
						</v-row>
						<v-row no-gutters align="center" justify="center" class="my-2">
							<Signature ref="signature" style="border: 1px solid gray" />
						</v-row>
					</v-card-text>
					<v-divider></v-divider>
					<v-card-actions>
						<v-btn color="primary darken-1" text @click="showConsent = false">
							<translate key="close">Close</translate>
						</v-btn>
						<v-spacer></v-spacer>
						<v-btn color="primary darken-1" text @click="generateConsentFile">
							<translate>Print</translate>
						</v-btn>
						<v-btn color="primary darken-1" text @click="saveConsent" :loading="loadingSaveConsent">
							<translate>Save</translate>
						</v-btn>
					</v-card-actions>
				</v-card>
			</v-dialog>
			<v-row no-gutters class="mt-2">
				<v-col cols="12" class="px-3">
					<v-row no-gutters align="center" class="mt-3 mb-3">
						<span class="text-subtitle-1 font-weight-regular primary--text" v-translate key="patient-info">Patient Data</span>
					</v-row>
					<v-divider class="mb-0"></v-divider>
					<v-btn color="primary" class="mt-4 ml-2" @click="showDeletePatient = true" :loading="loading">
						<v-icon left>mdi-delete</v-icon>
						<translate>Delete</translate>
						<DeleteConfirmationDialog v-model="showDeletePatient" item="patient" @delete="deactivatePatient" />
					</v-btn>
				</v-col>
			</v-row>
			<v-row no-gutters class="px-3 mt-4">
				<v-col cols="12">
					<v-row no-gutters align="center" class="mt-3 mb-3">
						<span class="text-subtitle-1 font-weight-regular primary--text" v-translate key="patient-info">Facturation</span>
					</v-row>
					<v-divider class="mb-0"></v-divider>
				</v-col>
				<v-col cols="12" class="mt-3">
				<p v-if="appointments.length == 0" class="font-weight-thin ml-2 mt-6">No appointments.</p>
				<v-list v-else flat>
					<v-card class="mx-auto mb-1" v-for="(appointment) in appointments" :key="appointment.id" elevation="1">
						<v-list-item
							three-line
							color="primary"
							:style="!appointment.effective ? {opacity: 0.7} : {}"
						>
							<v-list-item-content>
								<v-list-item-title class="primary--text">
									<v-row no-gutters class="text-body-1">
										<v-icon class="mr-1" small color="primary">mdi-account-tie</v-icon>
										<span class="mr-2">{{appointment.user.name}}</span>
										<v-icon class="mr-1" small color="primary">mdi-certificate</v-icon>
										<span>{{appointment.speciality.name}}</span>
										<v-spacer></v-spacer>
										<v-chip class="mr-2" small v-if="!appointment.effective"><translate>Scheduled</translate></v-chip>
										<v-icon class="mr-1" small color="primary">mdi-calendar</v-icon>
										<span class="mr-2">{{appointment.date}}</span>
										<v-icon class="mr-1" small color="primary">mdi-clock-outline</v-icon>
										<span>{{appointment.time}}</span>
									</v-row>
								</v-list-item-title>
								<v-list-item-subtitle>
									<v-row no-gutters>
									{{appointment.description}}
									<v-spacer></v-spacer>
									<span v-if="appointment.price" class="text--overline font-weight-bold primary--text">
										{{appointment.price}}€
									</span>
									</v-row>
								</v-list-item-subtitle>
							</v-list-item-content>
						</v-list-item>
					</v-card>
				</v-list>
				</v-col>
			</v-row>
		</v-container>
	</div>
</template>

<script>
import DeleteConfirmationDialog from "@/components/DeleteConfirmationDialog.vue";
import ConsentItem from "@/components/patient/ConsentItem.vue";
import RichTextEditor from "@/components/RichTextEditor.vue";
import Signature from "@/components/drawing/Signature.vue";
import axios from "axios";

export default {
	name: "PatientManagement",
	props: ["patient"],
	components: {
		DeleteConfirmationDialog,
		RichTextEditor,
		Signature,
		ConsentItem
	},
	data: () => ({
		appointments: [],
		showDeletePatient: false,
		loadingPage: false,
		specialities: [],
		preMadeConsents: [],
		terapeuticConsent: null,
		dataConsent: null,
		loading: false,
		loadingConsent: false,
		newDataConsent: false,
		showDataUpload: false,
		newTerapeuticConsent: false,
		showTerapeuticUpload: false,
		showConsent: false,
		loadingSaveConsent: false,
		body: "",
		fileRecords: [],
		fileRecordsForUpload: [],
		consentType: 'data',
		valid: false,
		dateMenu: false,
		date: "",
		legalRepresentative: false
	}),
	computed: {
		existDataConsent() {
			if(this.patient.data_consent_blob) return true;
			else return false;
		},
		existTerapeuticConsent() {
			if(this.patient.terapeutic_consents.length > 0) return true;
			else return false;
		},
		age() {
			if (this.patient.date_birth) {
				const today = new Date();
				const birthdate = new Date(this.patient.date_birth);
				var ageDifMs = today - birthdate;
				var ageDate = new Date(ageDifMs);
				return Math.abs(ageDate.getUTCFullYear() - 1970);
			} else {
				return null;
			}
		},
		nameRules() {
			return [v => !!v || this.$gettext("Name is required")];
		},
		isMinor() {
			if(this.age && this.age < 16) return true;
			return false;
		},
		consentHeader() {
			var text = this.patient.name + ", ";
			if(this.patient.fiscal) text = text + "paciente com o NIF " + this.patient.fiscal + ", ";
			if(this.patient.health_system) text = text + "número de utente " + this.patient.health_system;
			return text;
		},
		types() {
			var types = [];
			for(let i = 0; i < this.specialities.length; i++) {
				types.push({
					title: this.specialities[i].name,
					consent_text: this.specialities[i].terapeutic_consent_text,
					consent_minor_text: this.specialities[i].terapeutic_consent_minor_text
				})
			}
			for(let i = 0; i < this.preMadeConsents.length; i++) {
				types.push({
					title: this.preMadeConsents[i].title,
					consent_text: this.preMadeConsents[i].consent_text,
					consent_minor_text: this.preMadeConsents[i].consent_minor_text
				})
			}
			types.push({
				title: this.$gettext("Other"),
				consent_text: "",
				consent_minor_text: ""
			})
			return types;
		},
		title() {
			if(this.consentType == 'data') return null;
			else {
				if(this.terapeuticConsent) return this.terapeuticConsent.title;
			}
			return null;
		},
		rules() {
			return [v => !!v || this.$gettext("Required")];
		}
	},
	methods: {
		deactivatePatient() {
			const patientId = this.$route.params.pid;
			this.loading = true;
			axios
				.post("/patient/" + patientId + "/change-status")
				.then(response => {
					console.log(response.data);
					if (response.status === 200) {
						this.loading = false;
						this.$router.push({
							name: "patients"
						});
					}
				})
				.catch(error => {
					console.log(error);
					this.loading = false;
				});
		},
		deletePatient() {
			const patientId = this.$route.params.pid;
			this.loading = true;
			axios
				.post("/patient/" + patientId + "/delete")
				.then(response => {
					console.log(response.data);
					if (response.status === 200) {
						this.loading = false;
						if (response.data.result === "deleted") {
							this.$router.push({
								name: "patients"
							});
						}
					}
				})
				.catch(error => {
					console.log(error);
					this.loading = false;
				});
		},
		validateForm() {
			return this.$refs.form.validate();
		},
		getConsentText() {
			if(this.consentType == 'terapeutic'){
				if(!this.isMinor) {
					this.body = this.terapeuticConsent.consent_text;
				} else {
					this.body = this.terapeuticConsent.consent_minor_text;
				}
			} else {
				if(!this.isMinor) {
					this.body = this.dataConsent.consent_text;
				} else {
					this.body = this.dataConsent.consent_minor_text;
				}
			}
			let component = this; 
			setTimeout(function() {
				component.$refs.editor.setContent(component.body);
				component.$refs.signature.clear();
			}, 50);
		},
		generateConsentFile() {
			if(this.validateForm()) {
				let patientId = this.patient.id;
				let form = new FormData();
				form.append("type", this.consentType);
				form.append("title", this.title);
				form.append('date', this.date);
				form.append("name", this.patient.name);
				if(this.patient.id_number) form.append("id_number", this.patient.id_number);
				if(this.patient.fiscal) form.append("fiscal", this.patient.fiscal);
				if(this.legalRepresentative) {
					form.append("is_minor", true);
					if(this.patient.representative_name) form.append("representative_name", this.patient.representative_name);
					if(this.patient.representative_id_number) form.append("representative_id_number", this.patient.representative_id_number);
					if(this.patient.representative_fiscal) form.append("representative_fiscal", this.patient.representative_fiscal);
				} else {
					form.append("is_minor", false);
				}
				form.append("html", this.$refs.editor.getHTML());
				let signature = this.getSignature();
				if(signature) {
					form.append("signature", signature);
				}

				let htmlForm = document.createElement("form");
				htmlForm.method = "POST";
				htmlForm.action = 
					"https://api.naltio.com/patient/" +
					//"http://127.0.0.1:5500/patient/" +
					patientId +
					"/generate-consent";
				htmlForm.target = "_blank";

				for (let field of form) {
					let f = document.createElement("input");
					f.name = field[0];
					f.value = field[1];
					htmlForm.appendChild(f);
				}
				document.body.appendChild(htmlForm);
				htmlForm.submit();
			}
		},
		filesSelected(fileRecordsNewlySelected) {
      var validFileRecords = fileRecordsNewlySelected.filter((fileRecord) => !fileRecord.error);
      this.fileRecordsForUpload = validFileRecords;
      if(this.fileRecordsForUpload.length > 0) this.uploadConsent();
		},
		onBeforeDelete(fileRecord) {
			var h = this.fileRecords.indexOf(fileRecord);
      if (h !== -1) {
				this.fileRecords.splice(h, 1);
			}
			var i = this.fileRecordsForUpload.indexOf(fileRecord);
      if (i !== -1) {
				this.fileRecordsForUpload.splice(i, 1);
			}
		},
		uploadConsent() {
			const patientId = this.$route.params.pid;
			let form = new FormData();
			form.append('type', this.consentType);
			form.append('consent', this.fileRecordsForUpload[0].file);
			form.append('title', this.title);
			form.append('date', this.date);
      axios
				.post("/patient/" + patientId + "/consent", form)
				.then(response => {
					if (response.data.result == 'added') {
						if(this.consentType == 'data') this.patient.data_consent_blob = response.data.blob;
						else this.patient.terapeutic_consents.push(response.data.consent);
						this.showDataUpload = false;
						this.showTerapeuticUpload = false;
						this.newDataConsent = false;
						this.newTerapeuticConsent = false;
          }
          this.fileRecords = [];
					this.fileRecordsForUpload = [];
				})
				.catch(error => {
          console.log(error);
				});
		},
		getSignature() {
			if(this.$refs.signature.isEmpty()) {
				return null;
			} else {
				return this.$refs.signature.save();
			}
		},
		saveConsent() {
			let signature = this.getSignature();
			let valid = this.validateForm();
			if(signature && valid) {
				this.loadingSaveConsent = true;
				let patientId = this.$route.params.pid;
				let form = new FormData();
				form.append("type", this.consentType);
				form.append("name", this.patient.name);
				form.append("title", this.title);
				form.append('date', this.date);
				if(this.patient.id_number) form.append("id_number", this.patient.id_number);
				if(this.patient.fiscal) form.append("fiscal", this.patient.fiscal);
				if(this.legalRepresentative) {
					form.append("is_minor", true);
					if(this.patient.representative_name) form.append("representative_name", this.patient.representative_name);
					if(this.patient.representative_id_number) form.append("representative_id_number", this.patient.representative_id_number);
					if(this.patient.representative_fiscal) form.append("representative_fiscal", this.patient.representative_fiscal);
				} else {
					form.append("is_minor", false);
				}
				form.append("html", this.$refs.editor.getHTML());
				form.append("signature", signature);
				form.append("save", true);
				axios
					.post("/patient/" + patientId + "/generate-consent", form)
					.then(response => {
						console.log(response.data);
						this.showConsent = false;
						if (response.data.result == 'added') {
							if(this.consentType == 'data') this.patient.data_consent_blob = response.data.blob;
							else this.patient.terapeutic_consents.push(response.data.consent);
							this.showDataUpload = false;
							this.showTerapeuticUpload = false;
							this.newDataConsent = false;
							this.newTerapeuticConsent = false;
						}
						this.loadingSaveConsent = false;
					})
					.catch(error => {
						console.log(error);
						this.loadingSaveConsent = false;
					});
			}
		},
		clickConsent(type) {
			this.showDataUpload = false;
			this.showTerapeuticUpload = false;
			this.consentType = type;
			if(type == 'data') {
				this.showConsent = true;
				this.getConsentText();
			}
			else if(this.$refs.specForm.validate()) {
				this.showConsent = true;
				this.getConsentText();
			}
		},
		clickUpload(type) {
			if(type == 'data') {
				this.showTerapeuticUpload = false;
				this.showDataUpload = !this.showDataUpload;
			} else {
				if(this.$refs.specForm.validate()) {
					this.showDataUpload = false;
					this.showTerapeuticUpload = !this.showTerapeuticUpload;
				}
			}
		},
		deleteDataConsent() {
			this.patient.data_consent_blob = null;
		},
		deleteTerapeuticConsent(consent) {
			this.patient.terapeutic_consents.splice(this.patient.terapeutic_consents.indexOf(consent), 1);
		}
	},
	created() {
		const id = this.$route.params.pid;
			axios
				.get("/patient/" + id + "/management")
				.then(response => {
					console.log(response.data);
					if (response.status === 200) {
						this.appointments = response.data.appointments;
						this.patient.terapeutic_consents = response.data.terapeutic_consents;
						this.specialities = response.data.specialities;
						this.preMadeConsents = response.data.pre_made_consents;
						this.dataConsent = response.data.data_consent;
						this.loadingPage = false;
						const now = new Date();
						var mm = now.getMonth() + 1;
						if(mm.toString().length < 2) mm = "0" + mm;
						var dd = now.getDate();
						var yy = now.getFullYear();
						this.date = yy + "-" + mm + "-" + dd;
						if(this.isMinor) this.legalRepresentative = true;
					}
				})
				.catch(error => console.log(error));
	}
};
</script>